import type { CompanionState } from '@/lib/companion/types';
import type { CompanionActivity, CompanionEdge } from '@/lib/companion/companion-activity';

/** A collaborator clearing a folder fires one edge per document. This is a
 *  floor on how often the companion may react, not a queue — edges that land
 *  inside the window are discarded rather than deferred. */
export const EDGE_COOLDOWN_MS = 8_000;

export const EDGE_POSE: Record<CompanionEdge, CompanionState> = {
  'document.created': 'celebrating',
  'ai.finished': 'celebrating',
  'ai.needs-approval': 'peeking',
  'doc.deleted.remote': 'failed',
  'access.denied': 'failed',
  'tab.navigated': 'lookingAround',
};

export interface ReactionInput {
  activity: CompanionActivity;
  /** Observed on every beat rather than remembered: the end of a capture is
   *  reported by the synced row, and no local claim is cleared on every stop
   *  path. See lib/companion/read-recording-state. */
  recording: boolean;
  seenEdgeAt: number;
  lastReactionAt: number;
  now: number;
}

export type Reaction =
  | { kind: 'sustained'; state: CompanionState }
  | { kind: 'edge'; state: CompanionState; edgeAt: number }
  | { kind: 'none' };

export function decideReaction(
  { activity, recording, seenEdgeAt, lastReactionAt, now }: ReactionInput,
): Reaction {
  if (recording || activity.aiStatus === 'running') {
    return { kind: 'sustained', state: 'working' };
  }

  const edge = activity.lastEdge;
  if (!edge || edge.at === seenEdgeAt) return { kind: 'none' };
  if (now - lastReactionAt < EDGE_COOLDOWN_MS) return { kind: 'none' };

  return { kind: 'edge', state: EDGE_POSE[edge.kind], edgeAt: edge.at };
}
